import { useEffect, useState } from "react";
import {
  Phone, Mail, MapPin, Calendar, Clock, FileText, ExternalLink, Loader2,
  Zap, Flame, Home, Thermometer, Droplets, CookingPot, Plug, MessageSquare, AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />}
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground">{value}</p>
      </div>
    </div>
  );
}

export function ChipRow({ icon: Icon, label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="py-2">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />} {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => (
          <span key={it} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">{it}</span>
        ))}
      </div>
    </div>
  );
}

export function Section({ title, children }) {
  return (
    <div className="border-t border-slate-100 pt-4">
      <h3 className="mb-1 font-heading text-sm font-bold uppercase tracking-wide text-primary">{title}</h3>
      {children}
    </div>
  );
}

function PriceFile({ path, filename, fileUrlFor }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);
  const isPdf = (filename || "").toLowerCase().endsWith(".pdf");

  useEffect(() => {
    let revoke;
    let active = true;
    (async () => {
      try {
        const res = await api.get(fileUrlFor(path), { responseType: "blob" });
        const u = URL.createObjectURL(res.data);
        revoke = u;
        if (active) setUrl(u);
      } catch {
        if (active) setError(true);
      }
    })();
    return () => {
      active = false;
      if (revoke) URL.revokeObjectURL(revoke);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  if (error) return <p className="text-sm text-destructive">Impossible de charger le fichier.</p>;
  if (!url) return <div className="flex h-32 items-center justify-center rounded-lg bg-slate-100"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  if (isPdf) {
    return (
      <a href={url} target="_blank" rel="noreferrer" data-testid="lead-file-link"
        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-3 text-sm font-semibold text-primary hover:bg-slate-50">
        <FileText className="h-4 w-4" /> {filename} <ExternalLink className="h-3.5 w-3.5" />
      </a>
    );
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="lead-file-link" className="block overflow-hidden rounded-lg border border-slate-200">
      <img src={url} alt={filename} className="max-h-72 w-full object-contain bg-slate-50" />
    </a>
  );
}

export function LeadRecap({ lead, fileUrlFor }) {
  return (
    <div className="space-y-1" data-testid="lead-recap">
      {(lead.type_demande || lead.demande_urgente === "Oui") && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {lead.type_demande && (
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{lead.type_demande}</span>
          )}
          {lead.demande_urgente === "Oui" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-bold uppercase text-red-700">
              <AlertTriangle className="h-3.5 w-3.5" /> Demande urgente
            </span>
          )}
        </div>
      )}

      {lead.apporteur && (
        <div className="mb-2 rounded-lg bg-primary/[0.06] px-3 py-2 text-sm">
          <span className="font-semibold text-primary">Saisi sur le formulaire :</span> {lead.apporteur}
        </div>
      )}

      {(lead.rappel_creneau || lead.rappel_date) && (
        <div className="mb-2 flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/[0.05] px-3 py-2.5 text-sm">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">Rappel souhaité</p>
            <p className="font-semibold text-foreground">{[lead.rappel_creneau, lead.rappel_date].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
      )}

      <Section title="Contact">
        <InfoRow icon={Phone} label="Portable" value={lead.telephone_portable} />
        <InfoRow icon={Phone} label="Fixe" value={lead.telephone_fixe} />
        <InfoRow icon={Mail} label="Email" value={lead.email} />
        <InfoRow icon={MapPin} label="Adresse" value={[lead.adresse, lead.code_postal_ville].filter(Boolean).join(", ")} />
        <InfoRow icon={Calendar} label="Date de naissance" value={lead.date_naissance} />
        {lead.created_at && <InfoRow icon={Calendar} label="Reçu le" value={fmtDate(lead.created_at)} />}
      </Section>

      <Section title="Électricité">
        <InfoRow icon={Zap} label="Compteur" value={lead.elec_compteur} />
        <InfoRow label="Puissance" value={lead.elec_puissance} />
        <InfoRow label="PDL" value={lead.elec_pdl} />
        <InfoRow label="Fournisseur" value={lead.elec_fournisseur} />
        <ChipRow label="Options" items={lead.elec_options} />
      </Section>

      {(lead.gaz_type?.length || lead.gaz_fournisseur) && (
        <Section title="Gaz">
          <ChipRow icon={Flame} label="Type" items={lead.gaz_type} />
          <InfoRow label="Fournisseur" value={lead.gaz_fournisseur} />
        </Section>
      )}

      <Section title="Logement">
        <InfoRow icon={Home} label="Type" value={lead.logement_type} />
        <InfoRow label="Année de construction" value={lead.annee_construction} />
        <InfoRow label="Surface" value={lead.surface ? `${lead.surface} m²` : null} />
        <InfoRow label="Occupants" value={lead.nombre_occupants} />
        <InfoRow label="Présence" value={lead.presence} />
        <InfoRow label="DPE connu" value={lead.connait_dpe} />
        <InfoRow label="Classe DPE" value={lead.dpe_classe} />
      </Section>

      {(lead.chauffage?.length > 0 ||
        lead.eau_chaude?.length > 0 ||
        lead.cuisson?.length > 0 ||
        lead.equipements?.length > 0 ||
        lead.equipements_autres) && (
        <Section title="Énergie & équipements">
          <ChipRow icon={Thermometer} label="Chauffage" items={lead.chauffage} />
          <ChipRow icon={Droplets} label="Eau chaude" items={lead.eau_chaude} />
          <ChipRow icon={CookingPot} label="Cuisson" items={lead.cuisson} />
          <ChipRow icon={Plug} label="Équipements" items={lead.equipements} />
          <InfoRow label="Autres équipements" value={lead.equipements_autres} />
        </Section>
      )}

      {lead.prix_fichier_path && fileUrlFor && (
        <Section title="Capture des prix">
          <PriceFile path={lead.prix_fichier_path} filename={lead.prix_fichier_nom} fileUrlFor={fileUrlFor} />
        </Section>
      )}

      {lead.commentaires && (
        <Section title="Commentaires">
          <div className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
            <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <p className="whitespace-pre-wrap">{lead.commentaires}</p>
          </div>
        </Section>
      )}

      <Section title="Consentement">
        <p className="text-sm font-medium text-foreground">
          {lead.autorisation_donnees
            ? "✅ Accès aux données Enedis / GRDF autorisé"
            : "❌ Accès aux données non autorisé"}
        </p>
      </Section>
    </div>
  );
}
